import type { PayRequestId } from "@@/shared/pay-requests/pay-request-id";
import type { PaymentProvider } from "@@/shared/payments/payment-providers";
import type { Price} from "@@/shared/prices/price";
import { zeroPrice } from "@@/shared/prices/price";
import { subtractFromPrice } from "@@/shared/prices/subtract-from-price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import { partitionBy } from "@@/utilities/partition-by";
import { sortBy } from "@@/utilities/sort";
import type { OrderEvent } from "../../order-events/events/_order-event";
import type { OrderChangedEvent } from "../../order-events/events/order-changed-event/order-changed.event";
import type { OrderCreatedEvent } from "../../order-events/events/order-created.event";
import type {
    OrderPayRequestEvent} from "../../order-events/events/order-pay-request-events/_order-pay-request.event";
import {
    isOrderPayRequestEvent,
} from "../../order-events/events/order-pay-request-events/_order-pay-request.event";
import { isOrderPayRequestCancelledEvent } from "../../order-events/events/order-pay-request-events/order-pay-request-cancelled.event";
import type {
    OrderPayRequestFulfilledEvent} from "../../order-events/events/order-pay-request-events/order-pay-request-fulfilled.event";
import {
    isOrderPayRequestFulfilledEvent,
} from "../../order-events/events/order-pay-request-events/order-pay-request-fulfilled.event";
import { isOrderPayRequestPendingEvent } from "../../order-events/events/order-pay-request-events/order-pay-request-pending.event";
import type {
    OrderPaymentEvent} from "../../order-events/events/order-payment-events/_order-payment.event";
import {
    isOrderPaymentEvent,
} from "../../order-events/events/order-payment-events/_order-payment.event";
import type {
    OrderPaymentFulfilledEvent} from "../../order-events/events/order-payment-events/order-payment-fulfilled.event";
import {
    isOrderPaymentFulfilledEvent,
} from "../../order-events/events/order-payment-events/order-payment-fulfilled.event";
import { isOrderPaymentPendingEvent } from "../../order-events/events/order-payment-events/order-payment-pending.event";
import type { OrderPaymentRefundFulfilledEvent } from "../../order-events/events/order-payment-events/order-payment-refund-fulfilled.event";
import { legacyOrderPaidPrice } from "../shared/legacy-order-paid-price";

type PaymentProjectionEvent = OrderPaymentEvent | OrderPayRequestEvent;
const isPaymentProjectionEvent = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: any,
): event is PaymentProjectionEvent => {
    return isOrderPaymentEvent(event) || isOrderPayRequestEvent(event);
};

type PaymentStatusProjection = {
    event: OrderCreatedEvent | OrderChangedEvent;
    paymentFor: OrderEvent[];
    payFulfilledEvent?:
        | OrderPayRequestFulfilledEvent
        | OrderPaymentFulfilledEvent;
    paymentEvents: PaymentProjectionEvent[];
    amountPending: Price;
    amountPaid: Price;
    amountRefunded: Price;
    amountLeft: Price;
    paymentProvider: PaymentProvider;
    hasBeenCancelled: boolean;
    replacedBy?: PayRequestId;
};

const isOrderChangedARefund = (event: OrderChangedEvent) => {
    return (
        sumPrices(event.changes.map(change => change.price))
            .amountIncludingVat < 0
    );
};

//TODO : Used to calculate the amount paid and refunded per payment event, currently using the ordercreated and orderchanged as it used
//TODO :  in payment-modal but should be refactored to use the payment events instead of Created and Changed
const orderPaymentStatusProjectionFactory = (events: OrderEvent[]) => {
    // This is a bit of a mess, but it's a mess that works.

    const createdEvent = events.find(
        e => e.type === "CREATED",
    ) as OrderCreatedEvent;

    const changedEvents = events.filter(
        r => r.type === "ORDER_CHANGED",
    ) as OrderChangedEvent[];

    const projections = [createdEvent, ...changedEvents].map(event =>
        projectionPerEvent(event, events),
    );

    return projections.filter(
        p => !p.hasBeenCancelled && p.paymentEvents.length > 0,
    );
};

const projectionPerEvent = (
    initiatingEvent: OrderCreatedEvent | OrderChangedEvent,
    events: OrderEvent[],
) => {
    const paymentEvents = events.filter(isPaymentProjectionEvent);
    const allPayEvents = paymentEvents.filter(event => {
        if (isOrderPayRequestEvent(event)) {
            return event.paymentFor.orderEventIds?.includes(
                initiatingEvent._id,
            );
        }

        if (Array.isArray(event.paymentFor.orderEventId)) {
            return event.paymentFor.orderEventId.includes(initiatingEvent._id);
        }

        return event.paymentFor.orderEventId === initiatingEvent._id;
    });

    const hasBeenCancelled = allPayEvents.find(e =>
        isOrderPayRequestCancelledEvent(e),
    );

    if (hasBeenCancelled && isOrderPayRequestCancelledEvent(hasBeenCancelled)) {
        const projection: PaymentStatusProjection = {
            event: initiatingEvent,
            paymentFor: [initiatingEvent],
            payFulfilledEvent: undefined,
            paymentEvents: allPayEvents,
            amountPaid: zeroPrice,
            amountPending: zeroPrice,
            amountRefunded: zeroPrice,
            amountLeft: zeroPrice,
            paymentProvider: "PAY_REQUEST",
            hasBeenCancelled: true,
            replacedBy: hasBeenCancelled.replacedBy,
        };
        return projection;
    }

    const paidOrderEvent = allPayEvents.find(
        item =>
            isOrderPaymentFulfilledEvent(item) ||
            isOrderPayRequestFulfilledEvent(item),
    );

    const paymentFor = events.filter(e => {
        if (!paidOrderEvent) return false;

        if (isOrderPayRequestEvent(paidOrderEvent)) {
            return paidOrderEvent.paymentFor.orderEventIds?.includes(e._id);
        }

        if (Array.isArray(paidOrderEvent.paymentFor.orderEventId)) {
            return paidOrderEvent.paymentFor.orderEventId.includes(e._id);
        }
        return false;
    });

    const refundEvents = events.filter(
        item =>
            item.type === "PAYMENT_REFUND_FULFILLED" &&
            ((paidOrderEvent?.type === "PAYMENT_FULFILLED" &&
                item.paymentUUID === paidOrderEvent.paymentUUID &&
                item.paymentProvider === paidOrderEvent.paymentProvider) ||
                (paidOrderEvent?.type === "PAY_REQUEST_FULFILLED" &&
                    item.paymentUUID ===
                        paidOrderEvent?.paymentProviderReference)),
    ) as OrderPaymentRefundFulfilledEvent[];

    const legacyPaidPrice = legacyOrderPaidPrice(initiatingEvent, events);

    const amountPaid =
        paidOrderEvent === undefined
            ? legacyPaidPrice.isLegacy
                ? legacyPaidPrice.price
                : zeroPrice
            : paidOrderEvent.type === "PAYMENT_FULFILLED"
              ? paidOrderEvent.paidAmount
              : paidOrderEvent.price;
    const amountRefunded = sumPrices(
        refundEvents.map(item => item.refundedAmount),
    );

    const pendingEvent =
        allPayEvents.find(isOrderPaymentPendingEvent) ||
        allPayEvents.find(isOrderPayRequestPendingEvent);

    const projection: PaymentStatusProjection = {
        event: initiatingEvent,
        paymentFor: paymentFor,
        payFulfilledEvent: paidOrderEvent,
        paymentEvents: allPayEvents,
        amountPaid,
        amountPending: paidOrderEvent
            ? zeroPrice
            : (pendingEvent?.price ?? zeroPrice),
        amountRefunded: amountRefunded,

        amountLeft: subtractFromPrice(amountPaid, [amountRefunded]),
        paymentProvider: paidOrderEvent?.paymentProvider ?? "STRIPE",
        hasBeenCancelled: false,
    };
    return projection;
};

type RefundItem = {
    paymentStatusProjection: PaymentStatusProjection;
    amount: Price;
    percentage: number;
};

const calculateRefunds = (
    payments: PaymentStatusProjection[],
    amountToRefund: Price | "FULL",
): RefundItem[] => {
    let remainingRefundAmount = amountToRefund;
    const totalAmountLeft = sumPrices(payments.map(p => p.amountLeft));

    if (remainingRefundAmount === "FULL") {
        return payments.flatMap(payment => {
            if (!payment.payFulfilledEvent) return [];

            return {
                paymentStatusProjection: payment,
                amount: payment.amountLeft,
                percentage:
                    payment.amountLeft.amountIncludingVat /
                    totalAmountLeft.amountIncludingVat,
            };
        });
    } else {
        const refunds: RefundItem[] = [];
        const totalRefund = remainingRefundAmount.amountIncludingVat;

        //Prioritize real payments over on premise payments
        const [onPremisePayments, realPayments] = partitionBy(
            payments,
            payment => payment.paymentProvider === "ON_PREMISE",
        );

        for (const payment of [
            ...realPayments.sort(sortBy(r => r.amountLeft.amountIncludingVat)),
            ...onPremisePayments,
        ]) {
            if (payment.amountLeft.amountIncludingVat === 0) {
                continue;
            }

            if (remainingRefundAmount.amountIncludingVat === 0) {
                break;
            }

            let currentRefundAmount = remainingRefundAmount;

            if (
                remainingRefundAmount.amountIncludingVat >
                payment.amountLeft.amountIncludingVat
            ) {
                currentRefundAmount = payment.amountLeft;
            }

            const percentage =
                currentRefundAmount.amountIncludingVat / totalRefund;

            refunds.push({
                paymentStatusProjection: payment,
                amount: currentRefundAmount,
                percentage,
            });
            remainingRefundAmount = subtractFromPrice(remainingRefundAmount, [
                currentRefundAmount,
            ]);
        }

        if (remainingRefundAmount.amountIncludingVat > 0) {
            throw new Error(
                "Not enough payments to refund the requested amount.",
            );
        }

        return refunds;
    }
};

export {
    calculateRefunds,
    isOrderChangedARefund,
    orderPaymentStatusProjectionFactory,
};
export type { PaymentStatusProjection, RefundItem };
